'use client';

import { cn } from '@/lib/utils';
import { Skeleton } from '@mui/material';
import Link from 'next/link';
import { useTranslation } from '@/lib/i18n';
import EventCard from '../cards/EventCard';
import NewsCard from '../cards/NewsCard';
import { logEvent } from '@/lib/firebase';
import { trackXAdsEvent } from '@/lib/xAds';
import { MarketCard, type MarketCardMarket } from '../cards/market';
import {
  findDisplayMarket,
  getMarketOutcome,
  getMarketStatus,
  isMarketResolved,
} from '../utils/market';

export type MarketEventResponse = {
  data: {
    feed: MarketEventFeedItem[];
    limit: number;
    offset: number;
    order_by: string;
    type: 'event';
  };
  success: boolean;
};

export type MarketEventFeedItem = {
  id: string;
  event_id: string;
  title: string;
  description: string;
  event_date: string;
  image_url: string;
  score: number;
  created_at: string;
  updated_at: string;
  latest_news_time: string;
  importance_score: number;
  category: string;
  news: MarketEventNewsItem[];
  relate_market: MarketEventRelateMarket[];
  social_signal?: MarketEventSocialSignal;
};

export type MarketEventSocialSignal = {
  total_interactions: number;
  change_in_24h?: number;
  history?: {
    observed_at: string | Date;
    total_interactions: number;
  }[];
};

export type MarketEventNewsItem = {
  news_id: string;
  news_title: string;
  source: string;
  source_icon_url: string;
  source_url?: string;
  summary: string;
  importance_level: number;
  is_breaking: boolean;
  is_hot: boolean;
  publish_time: string;
  events: MarketEventRawEvent[];
  recommended_questions?: string[];
};

export type MarketEventRawEvent = {
  id: string;
  slug: string;
  event_id: string;
  title: string;
  image: string;
  icon: string;
  total_volume: string;
  total_liquidity: string;
  end_date: string;
  markets: MarketEventRawMarket[];
  confidence: number;
  recommendation: string;
};

export type MarketEventRawMarket = {
  market_id: string;
  market_question: string;
  outcome_yes: string;
  outcome_no: string;
  outcome_yes_price: string;
  outcome_no_price: string;
  market_active: number;
  group_item_title: string;
  price_change_24h: string | null;
  recommendation: string;
  market_image: string;
  market_icon: string;
  category: string;
  market_closed: 0 | 1;
  end_date: string;
  closed_time: string;
  total_volume_usdc: string;
  last_trade_price: string;
  created_at: string;
  data_updated_at: string;
  condition_id: string;
  clob_tokens_id_yes: string;
  volume_24h: string;
};

export type MarketEventRelateMarket = {
  id: string;
  slug: string;
  event_id: string;
  title: string;
  image: string;
  icon: string;
  total_volume: string;
  volume_24hr: string;
  total_liquidity: string;
  end_date: string;
  time_category?: string;
  category: string;
  closed_time: string;
  closed: 0 | 1;
  markets: MarketEventRawMarket[];
};

export default function MarketEventListWithSkeleton({
  loading,
  list,
  showMarket = true,
}: MarketEventListWithSkeletonProps) {
  if (list.length) {
    return (
      <div className="space-y-6">
        {list.map((item) => (
          <MarketEvent
            key={item.id}
            event={item}
            showMarket={showMarket}
            className="pb-6 border-b border-white/15 last:border-none"
          />
        ))}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <MarketEventLoadingHolder key={item} />
        ))}
      </div>
    );
  }
}

interface MarketEventProps {
  event: MarketEventFeedItem;
  className?: string;
  showMarket?: boolean;
}

export function MarketEvent({ event, className, showMarket = true }: MarketEventProps) {
  return (
    <div className={className}>
      <Link
        className="block mb-4"
        href={`/predic/event?id=${event.event_id}`}
        onClick={() => {
          logEvent('click_event_card', {
            event_id: event.event_id,
            event_title: event.title,
          });
          trackXAdsEvent('click_event_card', {
            event_id: event.event_id,
            event_title: event.title,
          });
        }}
      >
        <EventCard event={event} />
      </Link>
      {showMarket && event.relate_market.length > 0 && <MarketList list={event.relate_market} />}
      <NewsList news={event.news} importance={event.importance_score || 0} />
    </div>
  );
}

interface MarketEventListWithSkeletonProps {
  loading: boolean;
  list: MarketEventFeedItem[];
  showMarket?: boolean;
}

function NewsList({ news, importance }: { news: MarketEventNewsItem[]; importance: number }) {
  const displayNews = (news || []).slice(0, 3);

  const { t } = useTranslation();

  if (!displayNews.length) return null;

  return (
    <>
      <div className="pt-5">
        <h3 className="text-sm font-medium leading-none text-[#757575]">{t('Live Updates')}</h3>
        <div className="relative mt-3 before:absolute before:left-1 before:top-3 before:bottom-0 before:w-px before:min-h-[calc(100%)-12px] before:bg-[#4A4A4A]">
          {displayNews.map((item) => (
            <div
              className="relative grid grid-cols-[12px_minmax(0,1fr)] gap-x-3 pb-6 last:pb-0"
              key={item.news_id}
            >
              <span className="relative z-10 mt-1.5 size-2 rounded-full border border-[#6D6D6D] bg-black" />
              <NewsCard
                newsId={item.news_id}
                newsTitle={item.news_title}
                source={item.source}
                sourceIconUrl={item.source_icon_url}
                sourceUrl={item.source_url}
                publishTime={item.publish_time}
                importance={importance || item.importance_level}
                importanceAlign="between"
                questions={item.recommended_questions}
                clickable
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function MarketList({ list }: { list: MarketEventRelateMarket[] }) {
  const { t } = useTranslation();
  const marketList = list.slice(0, 2).map((item) => {
    const eventSlug = item.slug || item.event_id || item.id;

    return {
      id: item.event_id || item.id,
      href: `/predic/market?id=${eventSlug}`,
      market: toMarketCardData(item),
    };
  });

  return (
    <div>
      <div className="flex items-center justify-between text-sm text-[#757575] mb-4">
        <span>{t('Related Markets')}</span>
        {/* <ChevronRight size={20} /> */}
      </div>
      <div className="space-y-4">
        {marketList.map((item) => (
          <MarketCard
            key={item.id}
            marketEventId={item.id}
            market={item.market}
            href={item.href}
            showBookmark={false}
          />
        ))}
      </div>
    </div>
  );
}

function toMarketCardData(data: MarketEventRelateMarket): MarketCardMarket {
  const displayMarket = findDisplayMarket(data.markets, data.closed === 1);
  const eventTitle = data.title || displayMarket?.market_question || '';
  const poster =
    data.image || data.icon || displayMarket?.market_icon || displayMarket?.market_image || '';
  const yesPrice = toNumber(displayMarket?.outcome_yes_price);
  const change24h = toNumber(displayMarket?.price_change_24h ?? 0);
  const totalVolume = toNumber(data.volume_24hr || displayMarket?.volume_24h || 0);
  const groupItemTitle = displayMarket?.group_item_title || 'YES';
  const isEnded = data.closed === 1;

  return {
    marketEventId: data.event_id || data.id,
    imageSrc: poster,
    imageAlt: eventTitle,
    title: eventTitle,
    outcome: groupItemTitle,
    probability: yesPrice * 100,
    change: change24h * 100,
    changePeriod: '(24h)',
    volume: totalVolume,
    category: data.category || data.time_category || '',
    statusLabel: getMarketStatus(displayMarket),
    resolvedOutcome: isMarketResolved(displayMarket) ? getMarketOutcome(displayMarket) : undefined,
  };
}

function MarketEventLoadingHolder() {
  return (
    <div
      className={cn(
        'rounded-lg overflow-hidden w-full hover:opacity-90 transition-opacity',
        'py-4 px-3 space-y-3',
        'bg-[#121212]',
      )}
    >
      <div className="flex items-center gap-2.5">
        <Skeleton variant="rounded" width={44} height={44} sx={{ bgcolor: 'grey.800' }} />
        <div className="flex-1">
          <Skeleton variant="text" width="80%" sx={{ fontSize: '1rem', bgcolor: 'grey.800' }} />
          <Skeleton variant="text" width="60%" sx={{ fontSize: '1rem', bgcolor: 'grey.800' }} />
        </div>
      </div>

      <div className="text-xs flex items-center gap-2">
        <Skeleton variant="text" width={60} sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }} />
        <Skeleton variant="text" width={60} sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }} />
        <Skeleton variant="text" width={60} sx={{ fontSize: '0.75rem', bgcolor: 'grey.800' }} />
      </div>
    </div>
  );
}

function toNumber(value?: string | number) {
  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? 0 : numberValue;
}
